import { UnknownInputParams } from 'expo-router';
import { INCENTIVE_VARIATION } from 'features/v2a/receiver/constants/constants';
import { jwtDecode } from 'jwt-decode';
import { Linking } from 'react-native';
import {
  campaign as campaignParams,
  V2A_INCENTIVE_REFERRAL_SOURCE,
} from 'shared/constants/urlParams';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

export interface Agreement {
  htmlDocumentId: string;
  pdfDocumentId: string;
  htmlDocumentUrl: string;
  pdfDocumentUrl: string;
  textDocumentId: string;
  textDocumentUrl: string;
  explicitText: string;
  explicit: false;
  agreementId: string;
  type: string;
  category: string;
  documentId: string;
  documentUrl: string;
  name: string;
  isExplicit: boolean;
}

export interface AgreementsData {
  agreements: Agreement[];
}

export interface Data {
  card: Card;
  consent: Consent;
}

export interface Card {
  name: {
    firstName: string;
    lastName: string;
  };
  address: Address;
  email: string;
  cardCredentials: CardCredentials;
  zipCode: string;
}

export interface Address {
  state: string;
  postalCode: string;
}

export interface CardCredentials {
  cardNumber: string;
  expirationDate: {
    year: number;
    month: number;
  };
  cvv: string;
}

export interface V2ABankAccountForm {
  firstName: string;
  lastName: string;
  email: string;
  cardNumber: string;
  date: string;
  cvv: string;
  zipCode: string;
  state: string;
  isActiveTerms: boolean;
  isActiveMarketing: boolean;
}

export interface Consent {
  agreementIds: string[];
  implicitConsent: boolean;
}

export interface GetTokenResponse extends Response {
  data: GetTokenResult;
}

export interface GetTokenResult {
  token: string;
  amount: string;
  note: string;
  name: string;
  transferId: string;
}

export interface SuccessPagePayload {
  email: string;
}

export interface TransferError {
  errorCode: string;
  message: string;
  statusCode: string;
}

export interface ChallengeData {
  challengeId: string;
  challengeType: string;
  challengeContext: string;
  challengeState: string;
}
export interface TransferData {
  challenge: ChallengeData;
  status: string;
}
export interface ErrorMsgType {
  type: string;
  error: string;
  subtitle?: string;
  screenType?: ErrorScreenType;
}

export const getAgreementHref = (
  agreementUrls: Record<string, string>[],
  type: string,
): string => {
  return agreementUrls?.reduce((acc, item) => {
    if (type in item) {
      return item[type];
    }
    return acc;
  }, '');
};

export const defaultV2AProperties = (userId: string) => {
  return {
    flow_name: 'transfer_money_v2a_receiver',
    receiver_id: userId,
  };
};

export const decodeJWT = (tokenResponse: GetTokenResult) => {
  return jwtDecode(tokenResponse?.token) as Record<string, string>;
};

export const getCdnURL = (environment: string): string => {
  return `https://${environment}-cdn.varomoney.com`;
};

export const getV2AUserAgreementUrl = (environment: string): string =>
  `${getCdnURL(environment)}/agreements/VARO_TO_ANYONE_USER_AGREEMENT.pdf`;

export const formatExpDate = (expDate: string): string => {
  return expDate.replace(/^(\d{2})(\d{1,2})$/, '$1/$2');
};

// ERROR MAPPING
export interface TransferError {
  errorCode: string;
  message: string;
  statusCode: string;
}

export interface ChallengeData {
  challengeId: string;
  challengeType: string;
  challengeContext: string;
  challengeState: string;
}
export interface TransferData {
  challenge: ChallengeData;
  status: string;
}
export interface ErrorMsgType {
  type: string;
  error: string;
  subtitle?: string;
  screenType?: ErrorScreenType;
}

export const openAgreementLink = async (
  type: string,
  agreements: Agreement[] | undefined,
) => {
  const agreementPdfLink = agreements?.find(
    (agreement) => agreement.type === type,
  )?.pdfDocumentUrl;

  if (!agreementPdfLink) {
    return;
  }

  const canOpen = await Linking.canOpenURL(agreementPdfLink);
  if (canOpen) {
    await Linking.openURL(agreementPdfLink);
  }
};

export const TRANSFER_RESPONSE_STATUS = {
  CHALLENGE: 'CHALLENGE',
  COMPLETED: 'COMPLETED',
};

export const ErrorViewType = {
  INLINE: 'INLINE',
  TERMINAL: 'TERMINAL',
};

export const DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  cardNumber: '',
  date: '',
  cvv: '',
  zipCode: '',
  state: '',
  isActiveTerms: false,
  isActiveMarketing: false,
};

export const buildSignUpUrlParams = (
  paymentDetails: GetTokenResult,
): UnknownInputParams => {
  const decoded = decodeJWT(paymentDetails);
  const transferId = paymentDetails.transferId;
  const externalId = decoded?.rid as string;
  const email = decoded?.rme as string;
  const paramsObject: UnknownInputParams = {
    [campaignParams.source]: V2A_INCENTIVE_REFERRAL_SOURCE,
    [campaignParams.content]: externalId ?? '',
    [campaignParams.campaign]: INCENTIVE_VARIATION,
    [campaignParams.channel]: 'v2a_landing',
    [campaignParams.term]: transferId ?? '',
    ...(email ? { [campaignParams.email]: email } : {}),
  };
  return paramsObject;
};

export const socialLinks = {
  INSTAGRAM: 'https://www.instagram.com/varobank/',
  FACEBOOK: 'https://www.facebook.com/varomoney/',
  TWITTER: 'https://twitter.com/varobank',
  YOUTUBE: 'https://www.youtube.com/varomoney',
  LINKEDIN: 'https://www.linkedin.com/company/varobank/',
};

export const appLinks = {
  APPSTORE: 'https://apps.apple.com/us/app/varo-mobile-banking/id1517676784',
  PLAYSTORE: 'https://play.google.com/store/apps/details?id=com.varomoney.bank',
};

export const termsLinks = {
  TERMS: 'https://www.varomoney.com/privacy-legal/',
  PRIVACY: 'https://www.varomoney.com/privacy-legal/',
  LEGAL: 'https://www.varomoney.com/privacy-legal/',
};
